import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Input, Select, Textarea } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { remindersApi } from '../../api/reminders.api'
import { projectsApi } from '../../api/projects.api'
import { tasksApi } from '../../api/tasks.api'
import { rolesApi } from '../../api/roles.api'
import { useAuth } from '../../auth/AuthContext'
import { ApiError } from '../../api/client'
import { toast } from '../../store/toastStore'
import type { ReminderRepeat, ReminderTargetType } from '../../api/types'

const TARGET_OPTIONS: { value: ReminderTargetType; label: string }[] = [
  { value: 'PERSONAL', label: 'Persönlich' },
  { value: 'TASK', label: 'Aufgabe' },
  { value: 'PROJECT', label: 'Projekt' },
  { value: 'ROADMAP', label: 'Roadmap' },
]

const REPEAT_OPTIONS: { value: ReminderRepeat; label: string }[] = [
  { value: 'NONE', label: 'Einmalig' },
  { value: 'DAILY', label: 'Täglich' },
  { value: 'WEEKLY', label: 'Wöchentlich' },
  { value: 'MONTHLY', label: 'Monatlich' },
]

export function ReminderFormModal({ onClose }: { onClose: () => void }) {
  const { hasPermission } = useAuth()
  const queryClient = useQueryClient()
  const canManageOthers = hasPermission('reminders.manage_others')

  const [targetType, setTargetType] = useState<ReminderTargetType>('PERSONAL')
  const [targetId, setTargetId] = useState('')
  const [roleId, setRoleId] = useState('')
  const [message, setMessage] = useState('')
  const [dueAt, setDueAt] = useState('')
  const [repeat, setRepeat] = useState<ReminderRepeat>('NONE')
  const [error, setError] = useState<string | null>(null)

  const projectsQuery = useQuery({ queryKey: ['projects'], queryFn: async () => (await projectsApi.list({ pageSize: 100 })).data, enabled: targetType === 'PROJECT' })
  const tasksQuery = useQuery({ queryKey: ['tasks', { mine: false, pageSize: 200 }], queryFn: async () => (await tasksApi.list({ pageSize: 200 })).data, enabled: targetType === 'TASK' })
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: async () => (await rolesApi.list()).data, enabled: canManageOthers })

  const mutation = useMutation({
    mutationFn: () =>
      remindersApi.create({
        targetType,
        targetId: targetId || undefined,
        roleId: roleId || undefined,
        message,
        dueAt: new Date(dueAt).toISOString(),
        repeat,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      toast.success('Reminder angelegt.')
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Anlegen fehlgeschlagen.'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    mutation.mutate()
  }

  return (
    <Modal
      title="Neuer Reminder"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={mutation.isPending}>
            Anlegen
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}

        <Field label="Bezug" htmlFor="rem-target">
          <Select
            id="rem-target"
            value={targetType}
            onChange={(e) => {
              setTargetType(e.target.value as ReminderTargetType)
              setTargetId('')
            }}
          >
            {TARGET_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Field>

        {targetType === 'PROJECT' ? (
          <Field label="Projekt" htmlFor="rem-project">
            <Select id="rem-project" value={targetId} onChange={(e) => setTargetId(e.target.value)}>
              <option value="">Kein bestimmtes Projekt</option>
              {projectsQuery.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        {targetType === 'TASK' ? (
          <Field label="Aufgabe" htmlFor="rem-task">
            <Select id="rem-task" value={targetId} onChange={(e) => setTargetId(e.target.value)}>
              <option value="">Keine bestimmte Aufgabe</option>
              {tasksQuery.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        <Field label="Nachricht" htmlFor="rem-message">
          <Textarea id="rem-message" value={message} onChange={(e) => setMessage(e.target.value)} required />
        </Field>

        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <Field label="Fällig am" htmlFor="rem-due">
              <Input id="rem-due" type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} required />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Wiederholung" htmlFor="rem-repeat">
              <Select id="rem-repeat" value={repeat} onChange={(e) => setRepeat(e.target.value as ReminderRepeat)}>
                {REPEAT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>

        {canManageOthers ? (
          <Field label="Für eine Rolle (optional)" htmlFor="rem-role" hint="Alle Mitglieder dieser Rolle erhalten die Erinnerung">
            <Select id="rem-role" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
              <option value="">Nur für mich</option>
              {rolesQuery.data?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
      </form>
    </Modal>
  )
}
